import React, { useEffect, useState } from 'react';
import Header from '../Header';

// Existing APIs you already use
import { listReviewTypes, listPerformanceRatings, listMyKpiReviews } from '../../api/reviews';
import { upsertReview as upsertEmployeeReview } from '../../api/reviewsUpsert';

// KPA summary APIs & component (you already added earlier)
import { fetchKpaReviews, updateKpaEmployeeComment } from '../../api/kpaReviews';
import KPAReviewSummaryTable from '../../components/reviews/KPAReviewSummaryTable';
import { listKpas } from '../../api/kpas';
import { getMyContract } from '../../api/contracts';

// Company Value Reviews
import {
    listMyCompanyValueReviews,
    updateCompanyValueReviewEmployee,
} from '../../api/companyValueReviews';
import CompanyValuesTableEmployee from '../../components/reviews/CompanyValuesTableEmployee';
import CompanyValuesSummaryTable from '../../components/reviews/CompanyValuesSummaryTable';
import GeneralAssessment from '../../components/reviews/GeneralAssessment';
import {
    fetchMyGeneralAssessment,
    updateGeneralAssessmentEmployee,
} from '../../api/generalAssessments';

// Your existing KPI table component
import KPIReviewTable from '../../components/reviews/KPIReviewTable';

import '../../components/reviews/reviews-table.css';

//
import { getOverallRating, getOverallEligibility, updateOverallEmployee, acceptOverall } from '../../api/overallRatings';
import OverallRatingCard from '../../components/reviews/OverallRatingCard';

export default function MyPerformanceReviews() {
    const [reviewTypes, setReviewTypes] = useState([]);
    const [reviewTypeId, setReviewTypeId] = useState('');
    const [ratings, setRatings] = useState([]);

    const [contract, setContract] = useState(null);

    const [kpiRows, setKpiRows] = useState([]);
    const [kpaSummary, setKpaSummary] = useState([]);
    const [cvRows, setCvRows] = useState([]); // company values
    const [generalSummary, setGeneralSummary] = useState(null);

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const [overall, setOverall] = useState(null);
    const [overallElig, setOverallElig] = useState({ ok: false, errors: [] });


    const refreshOverall = async (cId = contract?.id, rtId = reviewTypeId) => {
        if (!cId || !String(rtId)) return;
        const res = await getOverallRating(cId, rtId);
        setOverall(res?.overall || null);
        const elig = await getOverallEligibility(cId);
        setOverallElig(elig);
    };

    useEffect(() => {
        (async () => { await refreshOverall(); })();
    }, [contract?.id, reviewTypeId]);


    const saveEmployeeOverall = async ({ rating_id, description }) => {
        if (!overall?.id) return;
        await updateOverallEmployee(overall.id, {
            employee_rating_id: rating_id,
            employee_description: description,
        });
        await refreshOverall();
    };

    const acceptAsEmployee = async () => {
        if (!overall?.id) return;
        await acceptOverall(overall.id);
        await refreshOverall();
    };

    // Fetch my contract on mount
    useEffect(() => {
        (async () => {
            try {
                const c = await getMyContract();
                setContract(c || null);
            } catch (e) {
                // no-op; modal will simply skip extra details loader
            }
        })();
    }, []);

    // Init lookups
    useEffect(() => {
        (async () => {
            try {
                const [rt, pr] = await Promise.all([
                    listReviewTypes(),
                    listPerformanceRatings(),
                ]);
                setReviewTypes(rt || []);
                setRatings(pr || []);
                const defId = rt?.[0]?.id ? String(rt[0].id) : '';
                setReviewTypeId(defId);
            } catch {
                setErr('Failed to initialize review types/ratings.');
            }
        })();
    }, []);

    // Load my KPI rows
    useEffect(() => {
        if (!reviewTypeId) return;
        (async () => {
            try {
                setLoading(true); setErr('');
                const kpiRes = await listMyKpiReviews(reviewTypeId);
                setKpiRows(kpiRes?.data || []);
            } catch (e) {
                setErr(e?.response?.data?.message || 'Failed to load KPI reviews.');
            } finally {
                setLoading(false);
            }
        })();
    }, [reviewTypeId]);

    // Load KPA summaries for me
    useEffect(() => {
        if (!reviewTypeId) return;
        (async () => {
            try {
                const res = await fetchKpaReviews({ reviewTypeId });
                setKpaSummary(res?.data || []);
            } catch (e) {
                console.warn('Failed to load KPA summaries (employee)', e?.response?.data || e);
            }
        })();
    }, [reviewTypeId]);

    // Load Company Value reviews for me
    useEffect(() => {
        if (!reviewTypeId) return;
        (async () => {
            try {
                const res = await listMyCompanyValueReviews(reviewTypeId);
                setCvRows(res?.data || []);
            } catch (e) {
                console.warn('Failed to load Company Value reviews (employee)', e?.response?.data || e);
            }
        })();
    }, [reviewTypeId]);

    // Load General Assessment summary (employee)
    useEffect(() => {
        if (!reviewTypeId) return;
        (async () => {
            try {
                const res = await fetchMyGeneralAssessment(reviewTypeId);
                setGeneralSummary(res?.summary || null);
            } catch (e) {
                console.warn('Failed to load General Assessment (employee)', e?.response?.data || e);
                setGeneralSummary(null);
            }
        })();
    }, [reviewTypeId]);

    // Save KPI row (employee side) then refresh KPI + KPA + CV lists
    const onSaveKpiRow = async ({ reviewId, kpiId, payload }) => {
        await upsertEmployeeReview({
            reviewId,
            reviewTypeId,
            kpiId,
            body: payload,
        });
        const [kpiRes, kpaRes, cvRes] = await Promise.all([
            listMyKpiReviews(reviewTypeId),
            fetchKpaReviews({ reviewTypeId }),
            listMyCompanyValueReviews(reviewTypeId),
        ]);
        setKpiRows(kpiRes?.data || []);
        setKpaSummary(kpaRes?.data || []);
        setCvRows(cvRes?.data || []);
        await refreshOverall();
    };

    // Save KPA employee comment
    const onSaveKpaEmployee = async ({ id, value }) => {
        await updateKpaEmployeeComment(id, value);
        const res = await fetchKpaReviews({ reviewTypeId });
        setKpaSummary(res?.data || []);
    };

    // Save Company Value (employee table row fields)
    const onSaveCompanyValueEmployee = async ({ reviewId, payload }) => {
        await updateCompanyValueReviewEmployee(reviewId, payload);
        const res = await listMyCompanyValueReviews(reviewTypeId);
        setCvRows(res?.data || []);
    };

    // ---- NEW: hook up CompanyValuesSummaryTable ----
    const onSaveCompanyValueComment = async ({ id, value }) => {
        // Employee page => update employee_comment
        await updateCompanyValueReviewEmployee(id, { employee_comment: value });
        const res = await listMyCompanyValueReviews(reviewTypeId);
        setCvRows(res?.data || []);
    };

    const onSaveGeneralAssessment = async ({ id, field, value }) => {
        if (!id) return;
        await updateGeneralAssessmentEmployee(id, { [field]: value });
        const res = await fetchMyGeneralAssessment(reviewTypeId);
        setGeneralSummary(res?.summary || null);
    };

    return (
        <>
            <Header label={<p className="py-0 my-0">My Performance Reviews</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">

                            <div className="card-header d-flex align-items-center gap-3">
                                <h5 className="mb-0">Review Type</h5>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ maxWidth: 260 }}
                                    value={reviewTypeId}
                                    onChange={(e) => setReviewTypeId(e.target.value)}
                                >
                                    {reviewTypes.map(rt => (
                                        <option key={rt.id} value={rt.id}>{rt.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="card-body">
                                {err && <div className="alert alert-danger">{err}</div>}

                                {/* KPI table */}
                                <KPIReviewTable
                                    rows={kpiRows}
                                    ratings={ratings}
                                    onSaveRow={onSaveKpiRow}
                                />

                                {/* KPA summary */}

                                <KPAReviewSummaryTable
                                    items={kpaSummary}
                                    role="employee"
                                    onSaveComment={onSaveKpaEmployee}
                                    contractId={contract?.id}
                                    loadKpaDetails={contract ? async (kpaId) => {
                                        const list = await listKpas(contract.id);
                                        return list.find(k => k.id === kpaId) || null;
                                    } : undefined}
                                />

                                {/* Company Value Reviews (Employee, detailed rows) */}

                                <CompanyValuesTableEmployee
                                    rows={cvRows}
                                    ratings={ratings}
                                    onSaveRow={onSaveCompanyValueEmployee}
                                    reviewTypeId={reviewTypeId}
                                />

                                {/* Company Values Summary (NEW) */}
                                <CompanyValuesSummaryTable
                                    items={cvRows}               // [{ company_value:{...}, review:{...} }]
                                    role="employee"              // this page is the employee view
                                    onSaveComment={onSaveCompanyValueComment}
                                    // Optional enrichment for the modal (not required):
                                    // loadValueDetails={(valueId) => api.get(`/api/company-values/${valueId}`).then(r => r.data)}
                                />

                                {/* General Assessment */}

                                <GeneralAssessment
                                    summary={generalSummary}
                                    role="employee"
                                    onSaveComment={onSaveGeneralAssessment}
                                />

                                {overall && (
                                    <OverallRatingCard
                                        overall={overall}
                                        reviewTypeName={reviewTypes.find(rt => String(rt.id) === String(reviewTypeId))?.name ?? ''}
                                        role="employee"
                                        ratings={ratings}
                                        eligibility={overallElig}
                                        onSaveEmployee={saveEmployeeOverall}
                                        onSaveManager={async () => {}}   // noop here
                                        onAccept={acceptAsEmployee}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
